import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

/**
 * Normalize string for search - removes diacritics and converts to lowercase.
 * Example: "Košice" → "kosice", "Životné" → "zivotne"
 */
export function normalizeSearch(str: string): string {
	return str
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase();
}

/**
 * Segment type for highlighted text
 */
export interface HighlightSegment {
	text: string;
	isMatch: boolean;
}

/**
 * Get text segments with match indicators for highlighting (case-insensitive, diacritics-aware)
 *
 * @param text - The text to search in
 * @param query - The search query to highlight
 * @returns Array of segments with isMatch flag
 *
 * @example
 * getHighlightSegments("Storm 0.5 GB", "storm")
 * // Returns: [{ text: "Storm", isMatch: true }, { text: " 0.5 GB", isMatch: false }]
 */
export function getHighlightSegments(text: string, query: string): HighlightSegment[] {
	if (!query || !text) return [{ text, isMatch: false }];

	// Normalize both for matching, but preserve original text for display
	const normalizedText = normalizeSearch(text);
	const normalizedQuery = normalizeSearch(query.trim());

	if (!normalizedQuery) return [{ text, isMatch: false }];

	// Find all match positions in normalized text
	const matches: Array<{ start: number; end: number }> = [];
	let searchPos = 0;

	while (searchPos < normalizedText.length) {
		const matchIndex = normalizedText.indexOf(normalizedQuery, searchPos);
		if (matchIndex === -1) break;

		matches.push({
			start: matchIndex,
			end: matchIndex + normalizedQuery.length,
		});
		searchPos = matchIndex + 1;
	}

	if (matches.length === 0) return [{ text, isMatch: false }];

	// Build result with segments
	const result: HighlightSegment[] = [];
	let lastEnd = 0;

	matches.forEach((match) => {
		// Add text before match
		if (match.start > lastEnd) {
			result.push({ text: text.slice(lastEnd, match.start), isMatch: false });
		}
		// Add matched text
		result.push({ text: text.slice(match.start, match.end), isMatch: true });
		lastEnd = match.end;
	});

	// Add remaining text after last match
	if (lastEnd < text.length) {
		result.push({ text: text.slice(lastEnd), isMatch: false });
	}

	return result;
}

/**
 * Check if current user has a specific capability
 */
export function userCan(capability: string): boolean {
	return window.dddData?.currentUser?.capabilities?.includes(capability) || false;
}

/**
 * Get current datetime in format required by datetime-local input
 * Format: "YYYY-MM-DDTHH:MM"
 */
export function getCurrentDateTime(): string {
	const now = new Date();
	const year = now.getFullYear();
	const month = String(now.getMonth() + 1).padStart(2, '0');
	const day = String(now.getDate()).padStart(2, '0');
	const hours = String(now.getHours()).padStart(2, '0');
	const minutes = String(now.getMinutes()).padStart(2, '0');

	return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Add minutes to a datetime string
 * @param datetime - datetime string in format "YYYY-MM-DDTHH:MM"
 * @param minutes - number of minutes to add
 * @returns datetime string in format "YYYY-MM-DDTHH:MM"
 */
export function addMinutes(datetime: string, minutes: number): string {
	const date = new Date(datetime);
	date.setMinutes(date.getMinutes() + minutes);

	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	const hours = String(date.getHours()).padStart(2, '0');
	const mins = String(date.getMinutes()).padStart(2, '0');

	return `${year}-${month}-${day}T${hours}:${mins}`;
}

/**
 * Format date string for display.
 *
 * Re-exported from date-formats.ts (SSOT).
 * @see {@link ./date-formats.ts} for all format options and locale overrides.
 */
export { formatDate } from './date-formats';

/**
 * Format full address string from parts (SSOT for all address display).
 * Floor is displayed as-is (user may type "3.", "prízemie", "suterén").
 * Apartment gets a localizable prefix (default "byt").
 *
 * Accepts both short keys (street, zip, city) and DB-prefixed keys (address_street, address_zip, address_city).
 */
export function formatFullAddress(parts: {
	street?: string | null;
	address_street?: string | null;
	floor?: string | null;
	apartment?: string | null;
	zip?: string | null;
	address_zip?: string | null;
	city?: string | null;
	address_city?: string | null;
	apartmentLabel?: string;
}): string {
	const street = parts.street || parts.address_street;
	const floor = parts.floor;
	const apartment = parts.apartment;
	const zip = parts.zip || parts.address_zip;
	const city = parts.city || parts.address_city;
	const apartmentLabel = parts.apartmentLabel || 'byt';
	const locationParts = [
		street || null,
		floor || null,
		apartment ? `${apartmentLabel} ${apartment}` : null,
	].filter(Boolean);
	const streetPart = locationParts.join(', ');
	const cityPart = [zip, city].filter(Boolean).join(' ');
	return [streetPart, cityPart].filter(Boolean).join(', ');
}

/**
 * Format work address from parts
 * @returns object with name and formatted address
 */
export function formatWorkAddress(
	name?: string | null,
	street?: string | null,
	zip?: string | null,
	city?: string | null,
	floor?: string | null,
	apartment?: string | null,
	apartmentLabel?: string
): { name: string; address: string } {
	return {
		name: name || '',
		address: formatFullAddress({ street, floor, apartment, zip, city, apartmentLabel }),
	};
}
